import { useState } from "react";
import { Save, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

export default function PromptEditor({ type, template, onSave }) {
    const original = template?.prompt_systeme ?? "";
    const [value, setValue] = useState(original);
    const [busy, setBusy] = useState(false);
    const dirty = value.trim() !== original.trim();

    async function submit(e) {
        e.preventDefault();
        if (!value.trim() || !dirty) return;
        setBusy(true);
        try {
            await onSave(type.value, value.trim());
        } catch (e) {
            console.error("Enregistrement du prompt impossible :", e);
        } finally {
            setBusy(false);
        }
    }

    return (
        <Card data-testid="prompt-editor" className="border-slate-200 bg-white shadow-sm">
            <CardContent className="pt-6 space-y-5">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Type de mandat</p>
                        <h2 data-testid="prompt-editor-title" className="mt-1 font-display text-2xl text-foreground">{type.label}</h2>
                    </div>
                    <div className="flex items-center gap-3">
                        <p data-testid="prompt-version-info" className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <History className="h-3.5 w-3.5" strokeWidth={1.75} />
                            {template
                                ? `Version du ${new Date(template.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}`
                                : "Aucun prompt personnalisé — prompt par défaut appliqué"}
                        </p>
                        <Button type="button" data-testid="prompt-save-button-top" onClick={submit} disabled={!dirty || !value.trim() || busy} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                            <Save className="h-4 w-4" strokeWidth={2} /> {busy ? "Enregistrement…" : "Enregistrer"}
                        </Button>
                    </div>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <Textarea
                        data-testid="prompt-textarea"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        rows={14}
                        placeholder="Décrivez le rôle de l'IA, la structure attendue de la synthèse, le ton et les garde-fous…"
                        className="bg-background text-sm leading-relaxed resize-y"
                    />
                    <div className="flex items-center justify-between">
                        <span data-testid="prompt-char-count" className="text-xs text-muted-foreground">{value.length} caractères</span>
                        <div className="flex gap-2">
                            {dirty && (
                                <Button type="button" variant="ghost" data-testid="prompt-cancel-button" onClick={() => setValue(original)} className="rounded-full text-muted-foreground">
                                    Annuler
                                </Button>
                            )}
                            <Button type="submit" data-testid="prompt-save-button" disabled={!dirty || !value.trim() || busy} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                                <Save className="h-4 w-4" strokeWidth={2} /> {busy ? "Enregistrement…" : "Enregistrer une nouvelle version"}
                            </Button>
                        </div>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
